import { useEffect, useRef, useState } from "react";
import LoadingOverlay from "../../../../../components/LoadingOverlay";
import { PdfGenerator } from "../../../../../components/PdfGenerator";
import PdfViewDrawer from "../../../../../components/PdfViewDrawer";
import { useLoadingContext } from "../../../../../context/LoadingProvider";
import useDisclosure, {
  DisclosureHost,
} from "../../../../../hooks/use-disclosure";
import { useEncounterSummeryV2 } from "../../../../../queries/encounter/use-encounter-summery-v2";
import EncounterSummeryTemplate, {
  useResolveEncounterLogo,
} from "../../../../../template/EncounterTemplate";
import { getXtenantId } from "../../../../../utils/utils";

/**
 * ViewEncounterDrawerProps interface to define the props for the ViewEncounterDrawer component
 *
 * @interface ViewEncounterDrawerProps
 * @property {any} appointmentId - Appointment ID
 */
interface ViewEncounterDrawerProps {
  disclosure: DisclosureHost;
  onClose?: () => void;
}

/**
 * Generates the legacy encounter-summary PDF and opens it in the viewer drawer.
 * Provider entry points go through `ViewEncounterSummaryDrawer`, which routes
 * Premier MD notes to the report preview and everything else here.
 *
 * @see disclosure {Must pass appointmentId in this disclosure EX- ` disclosure.open(appointmentId)`}
 *
 * @returns ViewEncounterDrawer component
 *
 * @author [Rajkumar Rathod]
 */
const LegacyEncounterSummaryDrawer = ({
  onClose,
  disclosure,
}: ViewEncounterDrawerProps) => {
  const pdfRef = useRef<{ generatePdf: (name?: string) => void }>(null);
  const { setIsLoading } = useLoadingContext();
  const {
    data: encounterPrefillData,
    isLoading,
    isPending,
    isFetching,
  } = useEncounterSummeryV2({
    appointmentUuid: disclosure?.data ?? "",
    xTENANTID: getXtenantId(),
    enabled: !!disclosure?.data && disclosure.opened,
  });
  const resolvedData = useResolveEncounterLogo(encounterPrefillData?.data);
  const [previewFile, setPreviewFile] = useState<string>("");
  const printViewDisclosure = useDisclosure(false, {
    onClose: disclosure.close,
  });

  useEffect(() => {
    if (resolvedData && !isLoading && !isFetching) {
      pdfRef?.current?.generatePdf();
    }
  }, [resolvedData, isLoading, isFetching]);

  if (!disclosure?.data || !disclosure.opened) return null;

  return (
    <>
      <LoadingOverlay isLoading={isFetching || isLoading || isPending} />

      {/* Hidden PDF component with prefilled data */}
      <PdfGenerator
        ref={pdfRef}
        onSave={(blob) => {
          setIsLoading(false);
          if (!blob) return;
          const url = window.URL.createObjectURL(blob);
          setPreviewFile(url);
          printViewDisclosure.open();
        }}
      >
        <EncounterSummeryTemplate data={resolvedData} />
      </PdfGenerator>

      <PdfViewDrawer
        w="65vw"
        file={previewFile}
        disclosureHost={printViewDisclosure}
        onClose={() => {
          onClose ? onClose() : disclosure.close();
        }}
        title="Visit"
      />
    </>
  );
};

export default LegacyEncounterSummaryDrawer;
